// Event-firing snippet for Unity / C#. Mirrors the Godot reference: fires
// one of the SDK event names you configured on the playtest flow, signs
// the request with HMAC-SHA256, and is a no-op until the player has
// paired (so it's safe to call from gameplay code without guarding).

export function buildEventUnitySnippet(apiKey: string, functionsBaseUrl: string): string {
  return `// Assets/PlayerBase/PlayerBaseEvents.cs
// Companion bootstrap singleton to PlayerBasePairing. Add it to the
// same GameObject (or a sibling) so both live on DontDestroyOnLoad and
// PlayerBaseEvents.Instance.IsPaired is ready when the first event
// fires.
//
// Call from anywhere in your game:
//
//   PlayerBaseEvents.Instance.Fire("tutorialComplete");
//   PlayerBaseEvents.Instance.Fire("bossDefeated",
//       new Dictionary<string, object> { { "boss", "stage1" }, { "tries", 4 } });
//
// Behaviour:
//   * No-op until the player has paired, so it's safe to call from
//     gameplay code without guarding.
//   * Generates a fresh idempotencyKey per fire, so retries collapse
//     server-side instead of double-counting.
//   * 200 covers ok / duplicate / no_active_playtest - all expected
//     during normal operation. Anything else logs a Debug.LogWarning
//     so config drift in dev builds is visible without crashing
//     gameplay.

using System;
using System.Collections;
using System.Collections.Generic;
using System.Security.Cryptography;
using System.Text;
using UnityEngine;
using UnityEngine.Networking;

public class PlayerBaseEvents : MonoBehaviour
{
    public static PlayerBaseEvents Instance { get; private set; }

    private const string ApiKey = "${apiKey}";
    private const string EventsUrl = "${functionsBaseUrl}/events";

    private void Awake()
    {
        if (Instance != null && Instance != this) { Destroy(gameObject); return; }
        Instance = this;
        DontDestroyOnLoad(gameObject);
    }

    public void Fire(string eventName, IDictionary<string, object> payload = null)
    {
        var pairing = PlayerBasePairing.Instance;
        if (pairing == null || !pairing.IsPaired) return;
        StartCoroutine(FireCoroutine(pairing.PlayerToken, eventName, payload));
    }

    private IEnumerator FireCoroutine(string playerToken, string eventName, IDictionary<string, object> payload)
    {
        string idempotencyKey = Guid.NewGuid().ToString();
        string occurredAt = DateTime.UtcNow.ToString("yyyy-MM-ddTHH:mm:ss.fffZ");
        string payloadJson = SerialiseJson(payload ?? new Dictionary<string, object>());

        var sb = new StringBuilder();
        sb.Append('{');
        AppendField(sb, "playerToken", playerToken); sb.Append(',');
        AppendField(sb, "eventName", eventName); sb.Append(',');
        AppendField(sb, "idempotencyKey", idempotencyKey); sb.Append(',');
        sb.Append("\\"payload\\":").Append(payloadJson).Append(',');
        AppendField(sb, "occurredAt", occurredAt);
        sb.Append('}');
        string body = sb.ToString();

        string signature = HmacSha256Hex(ApiKey, body);

        using var req = new UnityWebRequest(EventsUrl, "POST");
        req.uploadHandler = new UploadHandlerRaw(Encoding.UTF8.GetBytes(body));
        req.downloadHandler = new DownloadHandlerBuffer();
        req.SetRequestHeader("content-type", "application/json");
        req.SetRequestHeader("x-playerbase-signature", signature);

        yield return req.SendWebRequest();

        if (req.result != UnityWebRequest.Result.Success || req.responseCode != 200)
        {
            Debug.LogWarning(string.Format(
                "PlayerBase: {0} returned {1} ({2})",
                eventName, req.responseCode, req.error ?? "ok"));
        }
    }

    private static void AppendField(StringBuilder sb, string key, string value)
    {
        sb.Append('"').Append(key).Append("\\":\\"").Append(Escape(value)).Append('"');
    }

    // Tiny JSON encoder for the payload. Handles strings, numbers, bools,
    // null, IDictionary<string, object> and IList<object>. Everything else
    // is coerced to a string. Good enough for the small structured
    // payloads playtest events carry; swap in Newtonsoft.Json if you want
    // richer encoding.
    private static string SerialiseJson(object value)
    {
        if (value == null) return "null";
        if (value is bool b) return b ? "true" : "false";
        if (value is string s) return "\\"" + Escape(s) + "\\"";
        if (value is int || value is long || value is float || value is double || value is decimal)
            return Convert.ToString(value, System.Globalization.CultureInfo.InvariantCulture);
        if (value is IDictionary<string, object> dict)
        {
            var sb = new StringBuilder();
            sb.Append('{');
            bool first = true;
            foreach (var kv in dict)
            {
                if (!first) sb.Append(',');
                sb.Append('"').Append(Escape(kv.Key)).Append("\\":")
                  .Append(SerialiseJson(kv.Value));
                first = false;
            }
            sb.Append('}');
            return sb.ToString();
        }
        if (value is System.Collections.IEnumerable list)
        {
            var sb = new StringBuilder();
            sb.Append('[');
            bool first = true;
            foreach (var item in list)
            {
                if (!first) sb.Append(',');
                sb.Append(SerialiseJson(item));
                first = false;
            }
            sb.Append(']');
            return sb.ToString();
        }
        return "\\"" + Escape(value.ToString()) + "\\"";
    }

    private static string Escape(string s)
    {
        var sb = new StringBuilder(s.Length + 2);
        foreach (char c in s)
        {
            switch (c)
            {
                case '"': sb.Append("\\\\\\""); break;
                case '\\\\': sb.Append("\\\\\\\\"); break;
                case '\\n': sb.Append("\\\\n"); break;
                case '\\r': sb.Append("\\\\r"); break;
                case '\\t': sb.Append("\\\\t"); break;
                default:
                    if (c < 0x20) sb.AppendFormat("\\\\u{0:x4}", (int)c);
                    else sb.Append(c);
                    break;
            }
        }
        return sb.ToString();
    }

    private static string HmacSha256Hex(string key, string body)
    {
        using var h = new HMACSHA256(Encoding.UTF8.GetBytes(key));
        byte[] hash = h.ComputeHash(Encoding.UTF8.GetBytes(body));
        var sb = new StringBuilder(hash.Length * 2);
        foreach (byte b in hash) sb.Append(b.ToString("x2"));
        return sb.ToString();
    }
}
`;
}
